/**
 * DashboardPage — account overview landing screen after login.
 *
 * Greets the customer, surfaces their VIP membership status + savings, and
 * links to the key destinations (shop, account, order history). Collapsible
 * info tiles cover shipping and at-home safety.
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { useAuthCtx } from '../App';
import { useCurrency } from '../hooks/useCurrency';
import { formatDate } from '../utils/helpers';
import api from '../utils/api';

const SHIPPING_INFO = [
  {
    title: 'Guaranteed delivery',
    body: 'Every order is fully insured from dispatch to delivery. If your package is lost, damaged, or seized in transit, we will reship at no additional cost — no questions asked.',
  },
  {
    title: 'Estimated delivery window',
    body: 'We target 5–7 business days on all orders. Occasionally, supply-chain or customs processing may extend this to up to 15 days. You will be notified if your order is delayed beyond the standard window.',
  },
  {
    title: 'Discreet packaging',
    body: 'All orders ship in plain, unmarked packaging with no reference to Aloha Amino or product contents on the exterior. Return address reflects a neutral logistics partner.',
  },
  {
    title: 'Tracked shipments',
    body: 'A tracking number is emailed as soon as your order is dispatched. You can monitor progress end-to-end through your account dashboard under Order History.',
  },
  {
    title: 'Supply route integrity',
    body: 'We work exclusively with vetted, licensed logistics partners across our supply corridors. Cold-chain handling is applied where required to preserve compound integrity during transit.',
  },
  {
    title: 'International orders',
    body: 'Customs clearance timelines vary by destination. Import duties or local regulatory requirements are the responsibility of the recipient. Contact support before ordering if you are unsure of your jurisdiction\'s rules.',
  },
];

function ShippingInfoTile() {
  const [open, setOpen] = useState(false);
  return (
    <section className="mt-10">
      <button
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        className="w-full flex items-center justify-between px-5 py-4 rounded-xl border border-gray-200 bg-white shadow-sm hover:bg-gray-50 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <svg className="h-4 w-4 text-tea-500 shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path d="M6.5 3A1.5 1.5 0 0 0 5 4.5v.085A2.5 2.5 0 0 0 3 7v7a2.5 2.5 0 0 0 2.5 2.5h9A2.5 2.5 0 0 0 17 14V7a2.5 2.5 0 0 0-2-2.415V4.5A1.5 1.5 0 0 0 13.5 3h-7zm0 1.5h7v.5H6.5v-.5zM5 6.5h10A1 1 0 0 1 16 7.5v6a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-6A1 1 0 0 1 5 6.5zm2.25 2a.75.75 0 0 0 0 1.5h5.5a.75.75 0 0 0 0-1.5h-5.5z" />
          </svg>
          <span className="font-bold text-base text-gray-900">About shipping</span>
          <span className="ml-1 text-xs text-gray-400">Delivery windows, tracking, and package guarantee.</span>
        </div>
        <svg
          className={`h-4 w-4 text-gray-400 shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"
        >
          <path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06z" clipRule="evenodd" />
        </svg>
      </button>

      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${open ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}
      >
        <div className="mt-2 px-5 py-6 rounded-xl border border-gray-200 bg-white shadow-sm">
          <p className="text-sm text-gray-500 font-medium mb-5">
            We strive to deliver every order within 5–7 business days. All shipments are fully insured and tracked end-to-end.
          </p>
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-5">
            {SHIPPING_INFO.map(r => (
              <div key={r.title} className="flex gap-3">
                <span aria-hidden="true" className="mt-1 inline-block h-2 w-2 rounded-full bg-tea-500 shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900 text-sm">{r.title}</h3>
                  <p className="text-gray-600 text-sm mt-1 leading-relaxed">{r.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const HEALTH_RISKS = [
  {
    title: 'Dosing errors',
    body: 'Without clinical equipment and training, measuring precise doses is extremely difficult. Even small miscalculations can lead to severe overdose, underdose, or adverse reactions that require emergency medical attention.',
  },
  {
    title: 'Contamination risk',
    body: 'Home environments lack the sterile conditions required for handling research compounds. Improper storage or preparation can introduce bacteria, fungi, or other contaminants that cause serious infections.',
  },
  {
    title: 'Unknown interactions',
    body: 'Research compounds may interact unpredictably with prescription medications, supplements, or pre-existing health conditions. Only a qualified medical professional can assess these risks for an individual.',
  },
  {
    title: 'No emergency support',
    body: 'Adverse reactions can escalate rapidly. Without trained personnel and resuscitation equipment on hand, a reaction that would be manageable in a clinical setting can become life-threatening at home.',
  },
  {
    title: 'Injection site injury',
    body: 'Improper injection technique can cause abscesses, nerve damage, vascular injury, or the introduction of air emboli. These risks are substantially reduced only through formal clinical training.',
  },
  {
    title: 'Legal and liability exposure',
    body: 'Self-administering substances outside a licensed clinical framework may violate local health regulations and void any insurance coverage, leaving you fully liable for any resulting harm.',
  },
];

function HealthRisksTile() {
  const [open, setOpen] = useState(false);
  return (
    <section className="mt-3">
      <button
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        className="w-full flex items-center justify-between px-5 py-4 rounded-xl border border-red-100 bg-red-50 shadow-sm hover:bg-red-100 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <svg className="h-4 w-4 text-red-500 shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 5zm0 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2z" clipRule="evenodd" />
          </svg>
          <span className="font-bold text-base text-red-700">At-home treatment risks</span>
          <span className="text-xs text-red-400">Important safety information — please read before use.</span>
        </div>
        <svg
          className={`h-4 w-4 text-red-400 shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"
        >
          <path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06z" clipRule="evenodd" />
        </svg>
      </button>

      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${open ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}
      >
        <div className="mt-2 px-5 py-6 rounded-xl border border-red-100 bg-white shadow-sm">
          <p className="text-sm text-red-600 font-medium mb-5">
            Our products are intended strictly for research use by qualified professionals. The following risks apply to any unsupervised, at-home use of research compounds.
          </p>
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-5">
            {HEALTH_RISKS.map(r => (
              <div key={r.title} className="flex gap-3">
                <span aria-hidden="true" className="mt-1 inline-block h-2 w-2 rounded-full bg-red-400 shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900 text-sm">{r.title}</h3>
                  <p className="text-gray-600 text-sm mt-1 leading-relaxed">{r.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── VIP membership section ───────────────────────────────────────────────
 * Active members see their plan details + estimated lifetime savings.
 * Everyone else sees a focused upsell into the VIP membership.
 */
function StatBlock({ label, value, hint }) {
  return (
    <div className="rounded-xl bg-white/10 px-4 py-3.5">
      <div className="text-[11px] font-semibold uppercase tracking-widest text-tea-100">{label}</div>
      <div className="mt-1 text-2xl font-extrabold leading-none">{value}</div>
      {hint && <div className="mt-1 text-[11px] text-tea-100/80">{hint}</div>}
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-0">
      <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">{label}</span>
      <span className="text-sm font-semibold text-gray-800">{value}</span>
    </div>
  );
}

function VipSection() {
  const { user } = useAuthCtx();
  const { fmt } = useCurrency();
  const membership = user?.membership;
  const isMember = !!membership?.active;
  const [savings, setSavings] = useState(null);

  useEffect(() => {
    if (!isMember) return;
    api.get('/memberships/savings')
      .then(res => setSavings(res.data))
      .catch(() => { /* non-critical — section still renders without it */ });
  }, [isMember]);

  if (isMember) {
    const planLabel = membership.plan === 'monthly' ? 'Monthly VIP' : 'Annual VIP';
    const cadence = membership.plan === 'monthly' ? 'month' : 'year';
    return (
      <section className="mt-8">
        <div className="grid lg:grid-cols-5 gap-6">
          {/* Status + savings (brand panel) */}
          <div className="lg:col-span-3 rounded-2xl bg-gradient-to-br from-tea-600 to-tea-800 text-white p-6 sm:p-7 shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-tea-100">Membership</p>
                <h2 className="text-2xl font-extrabold mt-1">You're a VIP</h2>
              </div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-bold uppercase tracking-wider">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-300" /> Active
              </span>
            </div>
            <p className="mt-2 text-sm text-tea-100 leading-relaxed">
              Member pricing and free shipping are applied automatically on every order.
            </p>

            <div className="mt-5 grid grid-cols-3 gap-3">
              <StatBlock
                label="Total saved"
                value={savings ? fmt(savings.totalSavedCents) : '—'}
                hint="With VIP pricing"
              />
              <StatBlock
                label="On products"
                value={savings ? fmt(savings.productSavingsCents) : '—'}
                hint={savings ? `${savings.orderCount} orders` : ''}
              />
              <StatBlock
                label="On shipping"
                value={savings ? fmt(savings.shippingSavedCents) : '—'}
                hint={savings ? `${savings.freeShipOrders} free ships` : ''}
              />
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Link to="/shop" className="rounded-full bg-white text-tea-700 hover:bg-tea-50 text-sm font-bold px-6 py-2.5 text-center transition-colors">
                Shop with VIP pricing
              </Link>
              <Link to="/account" className="rounded-full border border-white/40 text-white hover:bg-white/10 text-sm font-bold px-6 py-2.5 text-center transition-colors">
                Manage membership
              </Link>
            </div>
          </div>

          {/* Plan details */}
          <div className="lg:col-span-2 card">
            <h3 className="text-lg font-bold mb-2">Your plan</h3>
            <DetailRow label="Plan" value={planLabel} />
            <DetailRow label="Fee" value={`${fmt(membership.priceInCents)} / ${cadence}`} />
            <DetailRow
              label={membership.autoRenew ? 'Renews' : 'Access until'}
              value={membership.currentPeriodEnd ? formatDate(membership.currentPeriodEnd) : '—'}
            />
            {membership.cardLast4 && (
              <DetailRow label="Card" value={`•••• ${membership.cardLast4}`} />
            )}
            {!membership.autoRenew && (
              <p className="mt-3 text-xs text-amber-600 font-medium">
                Auto-renew is off — your VIP access ends on the date above.
              </p>
            )}
          </div>
        </div>
      </section>
    );
  }

  // Non-member upsell
  return (
    <section className="mt-8">
      <div className="rounded-2xl bg-gradient-to-br from-tea-600 to-tea-800 text-white p-6 sm:p-8 shadow-md">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div className="max-w-2xl">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-tea-100">Aloha Amino VIP</p>
            <h2 className="text-2xl md:text-3xl font-extrabold mt-1 leading-tight">
              Unlock VIP pricing on every product
            </h2>
            <p className="mt-2 text-sm text-tea-100 leading-relaxed">
              Members pay the lowest price on every peptide and ship free on every order —
              with savings locked in even as retail prices rise. From just $5/month.
            </p>
            <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              {['VIP price on everything', 'Free shipping, always', 'Locked-in savings', 'Member-only early access'].map(b => (
                <li key={b} className="flex items-center gap-2">
                  <svg className="h-4 w-4 text-tea-100 shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0l-3.5-3.5a1 1 0 1 1 1.4-1.4l2.8 2.79 6.8-6.79a1 1 0 0 1 1.4 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-tea-50">{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="shrink-0">
            <Link to="/membership" className="block rounded-full bg-white text-tea-700 hover:bg-tea-50 text-sm font-bold px-8 py-3 text-center transition-colors shadow-sm">
              Become a VIP
            </Link>
            <p className="mt-2 text-center text-[11px] text-tea-100/80">Cancel anytime</p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function DashboardPage() {
  const { user } = useAuthCtx();

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 w-full">
        {/* ─── Greeting ─────────────────────────────────────────────────── */}
        <div className="mb-2">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
            Welcome back, {user?.firstName}
          </h1>
          <p className="text-gray-500 mt-1">Here's your account at a glance.</p>
        </div>

        {/* ─── VIP membership ───────────────────────────────────────────── */}
        <VipSection />

        {/* ─── Quick links ─────────────────────────────────────────────── */}
        <section className="mt-8 grid md:grid-cols-3 gap-6">
          <div className="card">
            <h2 className="text-lg font-bold mb-1">Browse the catalog</h2>
            <p className="text-gray-600 text-sm">
              Explore our research compounds and place a new order.
            </p>
            <Link to="/shop" className="btn-primary w-full mt-4 text-sm block text-center">
              Shop now →
            </Link>
          </div>
          <div className="card">
            <h2 className="text-lg font-bold mb-1">Order history</h2>
            <p className="text-gray-600 text-sm">
              Review past orders and tracking.
            </p>
            <Link to="/account" className="btn-secondary w-full mt-4 text-sm block text-center">
              View account
            </Link>
          </div>
          <div className="card">
            <h2 className="text-lg font-bold mb-1">Need a hand?</h2>
            <p className="text-gray-600 text-sm">
              Our team can help with orders, shipping, and product questions.
            </p>
            <Link to="/support" className="btn-secondary w-full mt-4 text-sm block text-center">
              Contact support
            </Link>
          </div>
        </section>

        {/* ─── Shipping + health risks — collapsible tiles ─────────────── */}
        <ShippingInfoTile />
        <HealthRisksTile />
      </main>
      <Footer />
    </div>
  );
}
